import React, { useEffect, useRef, useState } from "react";
import { adminClient, formatAdminDate } from "@/api/adminClient";
import { base44 } from "@/api/base44Client";
import { AdminListPager, AdminListToolbar } from "@/components/admin/AdminListToolbar";
import { useAdminListQuery } from "@/components/admin/useAdminListQuery";

export default function AdminVerification() {
  const [rows, setRows] = useState([]);
  const [notes, setNotes] = useState({});
  const [state, setState] = useState("loading");
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);
  const [busy, setBusy] = useState(null);
  const generation = useRef(0);
  const mounted = useRef(false);
  const busyRef = useRef(false);
  const actionGeneration = useRef(0);
  const controlsDisabled = state !== "ready" || !!busy;
  const { query, update, reset } = useAdminListQuery();

  async function load() {
    const current = ++generation.current;
    setState("loading");
    setError("");
    try {
      const params = new URLSearchParams({ paged: "true", limit: "25" });
      for (const key of ["q", "level", "offset"]) if (query[key]) params.set(key, query[key]);
      for (const key of ["from", "to"]) if (query[key]) params.set(key, new Date(`${query[key]}Z`).toISOString());
      const result = await adminClient(`/admin/verification-queue?${params}`);
      if (!mounted.current || current !== generation.current) return;
      setRows(result.items || []);
      setTotal(Number(result.total) || 0);
      setState("ready");
    } catch (e) {
      if (!mounted.current || current !== generation.current) return;
      setError(e.message);
      setState("error");
    }
  }
  useEffect(() => {
    mounted.current = true;
    load();
    return () => {
      mounted.current = false;
      generation.current += 1;
      actionGeneration.current += 1;
      busyRef.current = false;
    };
  }, [query.q, query.level, query.from, query.to, query.offset]);

  async function decide(id, decision) {
    if (busyRef.current || !mounted.current || state !== "ready") return;
    const action = ++actionGeneration.current;
    busyRef.current = true;
    setBusy(id);
    setError("");
    try {
      await base44.functions.invoke("reviewLevelAuthorisation", {
        id,
        decision,
        notes: notes[id] || "",
      });
      if (!mounted.current || action !== actionGeneration.current) return;
      await load();
    } catch (e) {
      if (mounted.current && action === actionGeneration.current)
        setError(e.message);
    } finally {
      if (mounted.current && action === actionGeneration.current) {
        busyRef.current = false;
        setBusy(null);
      }
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            Verification queue
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Review pending level authorisation requests with an auditable
            decision.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={state === "loading" || !!busy}
          className="min-h-11 rounded-lg border border-border px-3 py-2 text-sm font-semibold hover:bg-muted disabled:opacity-50"
        >
          Refresh
        </button>
      </div>
      <AdminListToolbar search={query.q || ""} searchLabel="Search organisation" total={total} loading={state === "loading"} onSearch={(q) => update({ q })} onReset={reset}>
        <label className="text-sm font-medium">Level<select aria-label="Filter verification level" value={query.level || "all"} onChange={(event) => update({ level: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm"><option value="all">All levels</option><option value="1">Level 1</option><option value="2">Level 2</option><option value="3">Level 3</option></select></label>
        <label className="text-sm font-medium">From (UTC)<input aria-label="Filter verification from date" type="datetime-local" value={query.from || ""} onChange={(event) => update({ from: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label>
        <label className="text-sm font-medium">To (UTC)<input aria-label="Filter verification to date" type="datetime-local" value={query.to || ""} onChange={(event) => update({ to: event.target.value })} className="mt-1 block min-h-11 rounded-lg border border-input bg-background px-3 py-2 text-sm" /></label>
      </AdminListToolbar>
      {error && (
        <p
          className="rounded-lg border border-destructive/40 bg-card p-4 text-sm text-destructive"
          role="alert"
        >
          {error}{" "}
          <button type="button" onClick={load} className="ml-2 underline">
            Retry
          </button>
        </p>
      )}
      <AdminListPager offset={Number(query.offset || 0)} limit={25} total={total} disabled={controlsDisabled} onPage={(offset) => update({ offset }, { resetOffset: false })} />
      {state === "loading" && (
        <p
          role="status"
          className="rounded-lg border border-border bg-card p-5 text-sm text-muted-foreground"
        >
          Loading verification requests…
        </p>
      )}
      {state !== "loading" && state === "ready" && rows.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-sm text-muted-foreground">
          Nothing waiting for verification.
        </p>
      ) : (
        rows.length > 0 && (
          <div className="space-y-3">
            {rows.map((row) => (
              <article
                key={row.id}
                className="rounded-xl border border-border bg-card p-4 shadow-sm"
              >
                <div className="flex flex-col gap-3 md:flex-row md:items-start">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-foreground">
                      {row.organisation || row.organisation_id} — Level{" "}
                      {row.level}
                    </h3>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Requested {formatAdminDate(row.requested_at)}
                    </p>
                  </div>
                  <label className="w-full md:max-w-sm">
                    <span className="sr-only">
                      Notes for {row.organisation || row.organisation_id}
                    </span>
                    <textarea
                      disabled={controlsDisabled}
                      value={notes[row.id] || ""}
                      onChange={(e) =>
                        setNotes((old) => ({
                          ...old,
                          [row.id]: e.target.value,
                        }))
                      }
                      placeholder="Notes to the organisation (optional)"
                      rows={2}
                      className="min-h-11 w-full resize-y rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring disabled:opacity-50"
                    />
                  </label>
                  <div className="flex shrink-0 gap-2">
                    <button
                      type="button"
                      disabled={controlsDisabled}
                      onClick={() => decide(row.id, "returned")}
                      className="min-h-11 rounded-lg border border-amber-300 px-3 py-2 text-sm font-semibold text-amber-800 hover:bg-amber-50 disabled:opacity-50"
                    >
                      Return
                    </button>
                    <button
                      type="button"
                      disabled={controlsDisabled}
                      onClick={() => decide(row.id, "approved")}
                      className="min-h-11 rounded-lg bg-teal-700 px-3 py-2 text-sm font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
                    >
                      {busy === row.id ? "Saving…" : "Approve"}
                    </button>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )
      )}
    </div>
  );
}
